import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Container, Text, TextInput, Select, Radio, Button, Group, Stack, Card, Badge, Divider, Alert, Loader, Flex, Title, Image,Center,SimpleGrid,Paper,Drawer,Input,CopyButton } from '@mantine/core';
import { useDocumentTitle } from '@mantine/hooks';
import { HeaderMenu } from '../../components/HeaderMenu/HeaderMenu';
import { IconAlertCircle, IconCheck, IconDownload } from '@tabler/icons-react';
import AdlookupApi from "../../api/AdLookupApi";
import { VisiLoader } from "../../components/GeneralComponent/GeneralComponent";
import visioptLogo from '../../img/logo-color.webp';
import { visiUserData } from '../../GeneralFunction';

export function AdLookup() {
  const navigate = useNavigate();
  useDocumentTitle('Visiopt - Ad Attribution Lookup');

  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [email, setEmail] = useState('');
  const [goalEvent, setGoalEvent] = useState('');
  const [decodedGoalEvent, setDecodedGoalEvent] = useState('');
  const [attributionModel, setAttributionModel] = useState('first_click');
  const [errors, setErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState<any>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [selectedAdPreview, setSelectedAdPreview] = useState<any>(null);
  const [webhookList, setWebhookList] = useState([]);
  const [SystemUsageLoader, setSystemUsageLoader] = useState(false);
  // const [expireTime, setExpireTime] = useState(null);
  // const [isExpired, setIsExpired] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<{ url: string } | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  let localvisiUserData = visiUserData();
  let member_id = localvisiUserData.member_id;

  const handleHeaderMenuComponentChange = (value: any) => {
    setSelectedMemberWebsite(value);
  };

  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // if either token or email query parameters are provided, call decode endpoint
    const tokenParam = searchParams.get("token");
    const emailParam = searchParams.get("email");
    if (!tokenParam && !emailParam) return;

    setSystemUsageLoader(true);
    AdlookupApi.decodemagiclink({
      token: tokenParam || '',
      email: emailParam || ''
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if (data.status === 1) {
        setEmail(data.data.email);
        setGoalEvent(data.data.goal_event);
        setDecodedGoalEvent(data.data.goal_event);
        setAttributionModel(data.data.attribution_model);
        setSelectedMemberWebsite(data.data.site);
        setSelectedMemberId(data.data.member_id);

        /*const expiry = Number(data.data.expire_time);
        const now = Math.floor(Date.now() / 1000);
        // Immediate expiry check
        if (expiry <= now) {
          setIsExpired(true);
          return;
        }
        setExpireTime(expiry);*/
        setSystemUsageLoader(false);
      } else {
        setSystemUsageLoader(false);
        console.log("Error:","Error");
      }
    })
    .catch(error => {
      setSystemUsageLoader(false);
      console.error("API Error:", error);
    });

  }, [searchParams]);

  const isMagicLink = location.pathname === "/ad-lookup" && !!searchParams.get("token");

  useEffect(() => {
    if(selectedMemberWebsite !== ''){
      AdlookupApi.getwebhooksitelist({
        system_website_id: selectedMemberWebsite,
        member_id: selectedMemberId || member_id
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if (data.status === 1) {
          const formattedList = data.data.webhook_list
            .filter(item => item.webhook_name && item.webhook_name.trim() !== '-' && item.webhook_name.trim() !== '')
            .map(item => ({
              value: item.webhook_id,
              label: item.webhook_name
            }));
          setWebhookList(formattedList);
          if (decodedGoalEvent) {
            setGoalEvent(decodedGoalEvent);
          }
        } else {
          console.log("Error:","Error");
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      });
    }
  }, [selectedMemberWebsite]);

  /*useEffect(() => {
    if (!expireTime) return;
    const now = Math.floor(Date.now() / 1000);
    const remainingMs = (expireTime - now) * 1000;
    if (remainingMs <= 0) {
      setIsExpired(true);
      return;
    }
    const timer = setTimeout(() => {
      setIsExpired(true);
    }, remainingMs);
    return () => clearTimeout(timer);
  }, [expireTime]);*/

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!email.trim()) {
      newErrors.push('Enter a lead\'s email and select what they did to see the ad that brought them to you.');
    } else if (!isValidEmail(email)) {
      newErrors.push('Enter a lead\'s email and select what they did to see the ad that brought them to you.');
    }

    if (!goalEvent) {
      newErrors.push('Enter a lead\'s email and select what they did to see the ad that brought them to you.');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleLookup = async () => {
    if (!validateForm()) return;
    setHasSearched(true);
    handleGenerateLink();
    setIsLoading(true);
    setErrors([]);
    try {
      const response = await AdlookupApi.getlookupattributionresult({
        email: email,
        goal_event: goalEvent,
        attribution_model: attributionModel,
        member_id: selectedMemberId || member_id
      });

      if (response.ok) {
        const data = await response.json();

        if (data.status === 1) {
          setLookupResult(data.data.ads_filter_data);
        } else {
          setErrors(["No attribution data found."]);
        }
      } else if (response.status === 401 || response.status === 400) {
        navigate("/logout");
      } else {
        setErrors(["Something went wrong. Please try again."]);
      }

    } catch (error) {
      console.error(error);
      setErrors(["API error. Please try again."]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateLink = () => {
    AdlookupApi.generatemagiclink({
      email: email,
      goal_event: goalEvent,
      attribution_model: attributionModel,
      site: selectedMemberWebsite,
      member_id: selectedMemberId || member_id
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if (data.status === 1) {
        const visi_token = data.data.visi_token;
        const link = `${window.location.origin}/ad-lookup?token=${visi_token}`;
        setGeneratedLink({ url: link });
      } else {
        console.log("Error:","Error");
      }
    })
    .catch(error => {
      console.error("API Error:", error);
    });
  };

  // Handle reset form
  const handleReset = () => {
    setEmail('');
    setGoalEvent('');
    setAttributionModel('first_click');
    setErrors([]);
    setLookupResult(null);
    setPreviewMode(false);
    setSelectedAdPreview(null);
    setGeneratedLink(null);
  };

  // Get attribution model display text and helper text
  const getAttributionModelInfo = (model: string) => {
    const models: Record<string, { label: string; helper: string }> = {
      first_click: {
        label: 'First Click',
        helper: 'Shows the first ad that introduced the lead to you'
      },
      last_click: {
        label: 'Last Click',
        helper: 'Shows the final ad the lead clicked before converting'
      }
    };
    return models[model] || { label: '', helper: '' };
  };

  // Handle ad name click to show preview
  const handleAdNameClick = async (platform,adid) => {
    try {
      setSystemUsageLoader(true);
      const response = await AdlookupApi.getattributionadspreviewdata({
        platform: platform,
        ad_id: adid,
        member_id: selectedMemberId || member_id
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
          return;
        }
        throw new Error('Failed to fetch ad preview');
      }

      const data = await response.json();

      if (data?.status === 1) {
        setSystemUsageLoader(false);
        setSelectedAdPreview(data.data);
        setPreviewMode(true);
      }
    } catch (err) {
      setSystemUsageLoader(false);
      console.error('Ad preview error:', err);
    } finally {
      setSystemUsageLoader(false);
    }
  };

  // Close ad preview
  const closeAdPreview = () => {
    setPreviewMode(false);
    setSelectedAdPreview(null);
  };

    // Helper to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  // Render Google text details (headline, description, primary_text)
  const renderGoogleTextDetails = (preview: any) => {
    const hasText = preview.headline || preview.description || preview.primary_text;
    if (!hasText) return null;
    return (
      <Stack spacing="sm">
        {preview.primary_text && (
          <Paper p="md" withBorder>
            <Text size="sm" color="dimmed" mb={4}>Primary Text</Text>
            <Text>{preview.primary_text}</Text>
          </Paper>
        )}
        {preview.headline && (
          <Paper p="md" withBorder>
            <Text size="sm" color="dimmed" mb={4}>Headline</Text>
            <Text weight={600} size="lg">{preview.headline}</Text>
          </Paper>
        )}
        {preview.description && (
          <Paper p="md" withBorder>
            <Text size="sm" color="dimmed" mb={4}>Description</Text>
            <Text>{preview.description}</Text>
          </Paper>
        )}
      </Stack>
    );
  };

  const renderPreview = () => {
    if (!selectedAdPreview?.preview) return null;

    const { preview } = selectedAdPreview;
    const platform = selectedAdPreview.platform;

    if (preview.type === 'html' || platform === 'facebook') {
      if (preview.html) {
        return (
          <Stack spacing="sm">
            <Paper p="md" withBorder>
              <div
                dangerouslySetInnerHTML={{ __html: preview.html }}
                style={{ width: '100%', minHeight: '400px' }}
              />
            </Paper>
            {preview.video_url && (
              <Button
                component="a"
                href={preview.video_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                leftIcon={<IconDownload size={16} />}
                variant="light"
              >
                Download Video
              </Button>
            )}
            {preview.image_url && (
              <Button
                component="a"
                href={preview.image_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                leftIcon={<IconDownload size={16} />}
                variant="light"
              >
                Download Image
              </Button>
            )}
            {platform === 'facebook' && !preview.video_url && !preview.image_url && (
              <Stack spacing={4}>
                <Button leftIcon={<IconDownload size={16} />} variant="light" disabled>
                  {preview.has_video ? 'Download Video' : 'Download'}
                </Button>
                <Text size="sm" color="dimmed">Facebook doesn't allow downloading this ad format.</Text>
              </Stack>
            )}
          </Stack>
        );
      }
      return <Text color="dimmed">No preview available</Text>;
    }

    switch (preview.type) {
      case 'image':
        return (
          <Stack spacing="sm">
            <Paper p="md" withBorder>
              {preview.media_url && (
                <Image
                  src={preview.media_url}
                  alt={selectedAdPreview.ad_name || "Ad Preview"}
                  radius="md"
                  fit="contain"
                  height={400}
                />
              )}
            </Paper>
            {preview.image_url && (
              <Button
                component="a"
                href={preview.image_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                leftIcon={<IconDownload size={16} />}
                variant="light"
              >
                Download Image
              </Button>
            )}
            {renderGoogleTextDetails(preview)}
          </Stack>
        );

      case 'video': {
        const youtubeEmbedUrl = preview.media_url
          ? getYouTubeEmbedUrl(preview.media_url)
          : null;

        return (
          <Stack spacing="sm">
            <Paper p="md" withBorder>
              {youtubeEmbedUrl ? (
                <iframe
                  src={youtubeEmbedUrl}
                  title="Ad Prview Player"
                  style={{ width: '100%', height: '400px', border: 'none' }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : preview.media_url ? (
                <video
                  src={preview.media_url}
                  controls
                  style={{ width: '100%', maxHeight: '500px' }}
                />
              ) : (
                <Text color="dimmed">Video preview not available</Text>
              )}
            </Paper>
            {renderGoogleTextDetails(preview)}
          </Stack>
        );
      }

      case 'text':
      default:
        return renderGoogleTextDetails(preview);
    }
  };

  /*if (isExpired) {
    return (
      <>
      <div
        style={{
          backgroundColor: "#0b0d3b",
          height: "55px",
          display: "flex",
          alignItems: "center",
          padding: "0 15px"
        }}
      >
        <img src={visioptLogo} alt="visilogo" style={{ width:"170", height:"33" }} />
      </div>
      <Container size="sm" py="xl" mt={20}>
        <Alert color="red" title="Link expired">
          Link expired — please reopen from CRM.
        </Alert>
      </Container>
      </>
    );
  }*/

  // Main form view
  return (
    <>
      {!isMagicLink ? (
        <HeaderMenu 
          HeaderMenuValue={selectedMemberWebsite} 
          HeaderMenuOnChange={handleHeaderMenuComponentChange}
        />
      ) : (
        <div
          style={{
            backgroundColor: "#0b0d3b",
            height: "55px",
            display: "flex",
            alignItems: "center",
            padding: "0 15px"
          }}
        >
          <img src={visioptLogo} alt="visilogo" style={{ width:"170", height:"33" }} />
        </div>
      )}

      <Container size={1000} py="xl">
        <div>
          <Title order={1} mb="xs">
            Where Did This Lead Come From?
          </Title>
          <Text color="dimmed" mb="xl">
            Instantly see the ad and message that influenced this lead before you speak with them.
          </Text>
        </div>

        <Stack spacing="xl">
          {/* Form Section */}
          <Card withBorder padding="xl" radius="md">
            <Stack spacing="md">
              {/* Email Input */}
              <TextInput
                label={
                  <>
                    Lead's Email Address <Text span color="red">*</Text>
                  </>
                }
                placeholder="Enter email address (full or masked, e.g. kl****01@gmail.com)"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={isMagicLink} 
              />

              {/* Goal Event Select */}
              <Select
                label={
                  <>
                    What did the lead do? <Text span color="red">*</Text>
                  </>
                }
                placeholder="Select a goal event"
                data={webhookList}
                value={goalEvent}
                onChange={setGoalEvent}
                searchable
                nothingFound="No goal events found"
                disabled={isMagicLink} 
              />

              {/* Attribution Model */}
              <div>
                <Text weight={600} size="sm" mb="md">
                  Attribution Model
                </Text>
                <Radio.Group
                  value={attributionModel}
                  onChange={setAttributionModel}
                >
                  <Stack spacing="xs">
                    <Radio
                      value="first_click"
                      label={
                        <div>
                          <Text weight={500} size="sm">
                            First Click
                          </Text>
                          <Text size="xs" color="dimmed">
                            Shows the first ad that introduced the lead to you
                          </Text>
                        </div>
                      }
                    />
                    <Radio
                      value="last_click"
                      label={
                        <div>
                          <Text weight={500} size="sm">
                            Last Click
                          </Text>
                          <Text size="xs" color="dimmed">
                            Shows the final ad the lead clicked before converting
                          </Text>
                        </div>
                      }
                    />
                  </Stack>
                </Radio.Group>
              </div>

              {/* Error Alert */}
              {errors.length > 0 && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  mb="md"
                >
                  {errors[0]}
                </Alert>
              )}

              {/* Buttons */}
              <Group>
                <Button
                  onClick={handleLookup}
                  loading={isLoading}
                  disabled={isLoading}
                  size="sm"
                >
                  Show Me the Ad
                </Button>

                {!isLoading && lookupResult && !isMagicLink &&(
                  <Button
                    variant="light"
                    size="sm"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                )}
              </Group>


              {/* Footer Text */}
              <Text size="xs" color="dimmed" align="center">
                Powered by server-side tracking… works even when browsers block analytics.
              </Text>
            </Stack>
          </Card>

          {/* Results Section */}
          {isLoading && (
            <Card withBorder padding="xl" radius="md">
              <Center py="xl">
                <Stack align="center" spacing="md">
                  <Loader />
                  <Text color="dimmed">Looking up attribution data...</Text>
                </Stack>
              </Center>
            </Card>
          )}
          <VisiLoader VisiLoaderVisible={SystemUsageLoader} />
          {!isLoading && (
          <>
            {!isMagicLink && generatedLink && lookupResult && lookupResult.length > 0 && (
              <Group mt={10}>
                <Input value={generatedLink.url} readOnly w={550} />
                <CopyButton value={generatedLink.url} timeout={2000}>
                  {({ copied, copy }) => (
                    <Button
                      color={copied ? "teal" : "blue"}
                      onClick={copy}
                      size="sm"
                    >
                      {copied ? "Copied Ad Lookup Link" : "Copy Ad Lookup Link"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            )}
            {!hasSearched ? (
              <Card withBorder padding="xl" radius="md">
                <Text align="center" color="dimmed">
                  Click "Show Me the Ad" to check the ad attribution result.
                </Text>
              </Card>
            ) : lookupResult && lookupResult.length > 0 ? (
              lookupResult.map((item, index) => (
              <Card key={index} withBorder padding="xl" radius="md" mb="md">
                <Stack spacing="md">

                  <Flex align="center" justify="space-between">
                    <Title order={3}>Here's the Ad</Title>
                    <Badge color="teal" leftSection={<IconCheck size={12} />}>
                      {getAttributionModelInfo(attributionModel).label}
                    </Badge>
                  </Flex>

                  <Divider />

                  {/* Traffic Source */}
                  <div>
                    <Text weight={600} size="sm" color="dimmed">
                      TRAFFIC SOURCE
                    </Text>
                    <Text size="sm">{item.traffic_source ? (String(item.traffic_source).toLowerCase() === 'chatgpt' ? 'ChatGPT' : item.traffic_source.charAt(0).toUpperCase() + item.traffic_source.slice(1)) : ""}</Text>
                  </div>

                  <Divider />

                  {/* ChatGPT is aggregate-only (no campaign/adset/ad hierarchy), so hide the "How This Lead Got Here" block for it. */}
                  {String(item.traffic_source || '').toLowerCase() !== 'chatgpt' && (
                  <>
                  <Text weight={500} size="sm">
                    How This Lead Got Here:
                  </Text>

                  {/* Campaign Name */}
                  <div>
                    <Text weight={600} size="sm" color="dimmed">
                      CAMPAIGN NAME
                    </Text>
                    <Text size="sm">{item.campaign_name}</Text>
                  </div>

                  {/* Ad Set Name */}
                  <div>
                    <Text weight={600} size="sm" color="dimmed">
                      AD SET NAME
                    </Text>
                    <Text size="sm">{item.adset_name}</Text>
                  </div>

                  {/* Ad Name */}
                  <div>
                    <Text weight={600} size="sm" color="dimmed">
                      AD NAME
                    </Text>
                    <Text
                      component="a"
                      size="sm"
                      color="blue"
                      weight={600}
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => handleAdNameClick(item.traffic_source,item.encode_ad_id)}
                    >
                      {item.ad_name}
                    </Text>
                  </div>

                  <Divider />
                  </>
                  )}

                  <SimpleGrid
                    cols={2}
                    breakpoints={[{ maxWidth: 'sm', cols: 1 }]}
                    spacing="lg"
                  >
                    {/* Goal Model Used */}
                    <div>
                      <Text weight={600} size="sm" color="dimmed" mb="xs">
                        Goal
                      </Text>
                      <Text size="sm">
                        {webhookList.find(item => item.value === goalEvent)?.label || "-"}
                      </Text>
                    </div>

                    {/* Conversion Date/Time */}
                    <div>
                      <Text weight={600} size="sm" color="dimmed" mb="xs">
                        CONVERSION DATE & TIME
                      </Text>
                      <Text size="sm">
                        {item.conversion_date}
                      </Text>
                    </div>

                  </SimpleGrid>

                </Stack>
              </Card>
            ))
            ) : (
              <Card withBorder padding="xl" radius="md">
                <Text align="center" color="dimmed">
                  No attribution data found.
                </Text>
              </Card>
            )}
          </>
        )}

        </Stack>
      </Container>

      <Drawer
          opened={previewMode}
          onClose={closeAdPreview}
          title="Ad Preview"
          position="right"
          size="lg"
          padding="md"
        >
          {selectedAdPreview && (
            <>
              {/* Header */}
              <Flex justify="space-between" align="center" mb="md">
                <Group>
                  <Title order={4}>{selectedAdPreview.ad_id}</Title>

                  <Badge color="red" variant="light" radius="sm">
                    {selectedAdPreview.platform ? selectedAdPreview.platform.charAt(0).toUpperCase() + selectedAdPreview.platform.slice(1) : ""}
                  </Badge>
                </Group>
              </Flex>

              {/* Info Card */}
              <Card withBorder radius="md" mb="lg">
                <Stack spacing="xs">
                  <Flex justify="space-between">
                    <Text color="dimmed">Status</Text>
                    <Badge color="green" variant="light">
                      {selectedAdPreview.status}
                    </Badge>
                  </Flex>

                  <Flex justify="space-between">
                    <Text color="dimmed">Platform</Text>
                    <Text>{selectedAdPreview.platform ? selectedAdPreview.platform.charAt(0).toUpperCase() + selectedAdPreview.platform.slice(1) : ""}</Text>
                  </Flex>

                  <Flex justify="space-between">
                    <Text color="dimmed">Ad ID</Text>
                    <Text>{selectedAdPreview.ad_id}</Text>
                  </Flex>
                </Stack>
              </Card>

              {/* Creative Preview */}
              <Title order={5} mb="md">
                Creative Preview
              </Title>

              {renderPreview()}
            </>
          )}
        </Drawer>
    </>
  );
}
